<script setup>
import { ref, reactive, computed } from "vue";
import { useI18n } from "vue-i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import AnimatedSection from "./AnimatedSection.vue";
import { Send, CheckCircle } from "lucide-vue-next";
import { z } from "zod";

const { t } = useI18n();

const contactSchema = computed(() =>
  z.object({
    name: z
      .string()
      .trim()
      .min(2, t("landing.contact.validation.name_min"))
      .max(100, t("landing.contact.validation.name_max")),
    email: z
      .string()
      .trim()
      .email(t("landing.contact.validation.email_invalid"))
      .max(255, t("landing.contact.validation.email_max")),
    company: z
      .string()
      .trim()
      .max(100, t("landing.contact.validation.company_max"))
      .optional(),
    requestType: z.enum(["demo", "trial"], {
      required_error: t("landing.contact.validation.request_type_required"),
    }),
    message: z
      .string()
      .trim()
      .max(1000, t("landing.contact.validation.message_max"))
      .optional(),
  })
);

const { toast } = useToast();
const isSubmitting = ref(false);
const isSubmitted = ref(false);
const errors = ref({});
const formData = reactive({
  name: "",
  email: "",
  company: "",
  requestType: "demo",
  message: "",
});
const honeypot = ref("");

const handleSubmit = async (e) => {
  if (e) e.preventDefault(); // Prevent default form submission
  errors.value = {};

  if (honeypot.value) {
    isSubmitted.value = true;
    return;
  }

  const result = contactSchema.value.safeParse(formData);
  if (!result.success) {
    const fieldErrors = {};
    result.error.errors.forEach((err) => {
      const field = err.path[0];
      fieldErrors[field] = err.message;
    });
    errors.value = fieldErrors;
    return;
  }

  isSubmitting.value = true;

  try {
    // Mock Supabase call - Replace with actual API call later
    await new Promise(resolve => setTimeout(resolve, 1500));

    isSubmitted.value = true;
    toast({
      title: t("landing.contact.toast.success_title"),
      description: formData.requestType === "demo"
        ? t("landing.contact.toast.success_demo")
        : t("landing.contact.toast.success_trial"),
    });

  } catch (err) {
    toast({
      title: t("landing.contact.toast.error_title"),
      description: t("landing.contact.toast.error_description"),
      variant: "destructive",
    });
  } finally {
    isSubmitting.value = false;
  }
};

const resetForm = () => {
  isSubmitted.value = false;
  honeypot.value = "";
  Object.assign(formData, {
    name: "",
    email: "",
    company: "",
    requestType: "demo",
    message: "",
  });
};
</script>

<template>
  <!-- Success State -->
  <section v-if="isSubmitted" id="contact-form" class="py-24 bg-background relative scroll-mt-20">
    <div class="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/20 to-transparent" />
    <div class="container mx-auto px-6">
      <AnimatedSection animation="scale" class="max-w-xl mx-auto text-center">
        <div class="p-12 rounded-xl border border-accent/30 bg-card/50 cyan-border-glow">
          <CheckCircle class="w-16 h-16 text-accent mx-auto mb-6" />
          <h3 class="text-2xl font-bold text-foreground mb-4">
            {{ t("landing.contact.success.title") }}
          </h3>
          <p class="text-muted-foreground mb-6">
            {{ t("landing.contact.success.description") }}
          </p>
          <Button
            variant="cyan-outline"
            @click="resetForm"
          >
            {{ t("landing.contact.success.send_another") }}
          </Button>
        </div>
      </AnimatedSection>
    </div>
  </section>

  <!-- Form State -->
  <section v-else id="contact-form" class="py-24 bg-background relative scroll-mt-20">
    <div class="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/20 to-transparent" />
    
    <div class="container mx-auto px-6">
      <!-- Section header -->
      <AnimatedSection animation="fade-up" class="text-center mb-12 max-w-2xl mx-auto">
        <h2 class="text-3xl md:text-4xl font-bold text-foreground mb-6">
          {{ t("landing.contact.title") }}
        </h2>
        <p class="text-lg text-muted-foreground">
          {{ t("landing.contact.subtitle") }}
        </p>
      </AnimatedSection>

      <!-- Contact form -->
      <AnimatedSection animation="fade-up" :delay="150" class="max-w-xl mx-auto">
        <form 
          @submit="handleSubmit"
          class="p-8 rounded-xl border border-border/50 bg-card/30 metallic-surface"
        >
          <!-- Honeypot field -->
          <div 
            class="absolute -left-[9999px] opacity-0 h-0 w-0 overflow-hidden"
            aria-hidden="true"
            tabindex="-1"
          >
            <label for="website">Website</label>
            <input
              type="text"
              id="website"
              name="website"
              autocomplete="off"
              v-model="honeypot"
              tabindex="-1"
            />
          </div>

          <!-- Request type -->
          <div class="mb-6">
            <Label class="text-foreground mb-3 block">{{ t("landing.contact.form.interest_label") }}</Label>
            <RadioGroup
              v-model="formData.requestType"
              class="flex gap-4"
            >
              <div class="flex items-center space-x-2">
                <RadioGroupItem value="demo" id="demo" class="border-accent text-accent" />
                <Label for="demo" class="text-muted-foreground cursor-pointer">
                  {{ t("landing.contact.form.option_demo") }}
                </Label>
              </div>
              <div class="flex items-center space-x-2">
                <RadioGroupItem value="trial" id="trial" class="border-accent text-accent" />
                <Label for="trial" class="text-muted-foreground cursor-pointer">
                  {{ t("landing.contact.form.option_trial") }}
                </Label>
              </div>
            </RadioGroup>
            <p v-if="errors.requestType" class="text-destructive text-sm mt-1">{{ errors.requestType }}</p>
          </div>

          <!-- Name -->
          <div class="mb-4">
            <Label for="name" class="text-foreground mb-2 block">
              {{ t("landing.contact.form.name_label") }}
            </Label>
            <Input
              id="name"
              type="text"
              v-model="formData.name"
              :placeholder="t('landing.contact.form.name_placeholder')"
              class="bg-background/50 border-border/50 focus:border-accent"
            />
            <p v-if="errors.name" class="text-destructive text-sm mt-1">{{ errors.name }}</p>
          </div>

          <!-- Email -->
          <div class="mb-4">
            <Label for="email" class="text-foreground mb-2 block">
              {{ t("landing.contact.form.email_label") }}
            </Label>
            <Input
              id="email"
              type="email"
              v-model="formData.email"
              :placeholder="t('landing.contact.form.email_placeholder')"
              class="bg-background/50 border-border/50 focus:border-accent"
            />
            <p v-if="errors.email" class="text-destructive text-sm mt-1">{{ errors.email }}</p>
          </div>

          <!-- Company -->
          <div class="mb-4">
            <Label for="company" class="text-foreground mb-2 block">
              {{ t("landing.contact.form.company_label") }}
            </Label>
            <Input
              id="company"
              type="text"
              v-model="formData.company"
              :placeholder="t('landing.contact.form.company_placeholder')"
              class="bg-background/50 border-border/50 focus:border-accent"
            />
            <p v-if="errors.company" class="text-destructive text-sm mt-1">{{ errors.company }}</p>
          </div>

          <!-- Message -->
          <div class="mb-6">
            <Label for="message" class="text-foreground mb-2 block">
              {{ t("landing.contact.form.message_label") }}
            </Label>
            <Textarea
              id="message"
              v-model="formData.message"
              :placeholder="t('landing.contact.form.message_placeholder')"
              rows="4"
              class="bg-background/50 border-border/50 focus:border-accent resize-none"
            />
            <p v-if="errors.message" class="text-destructive text-sm mt-1">{{ errors.message }}</p>
          </div>

          <!-- Submit button -->
          <Button
            type="submit"
            variant="hero-primary"
            class="w-full"
            :disabled="isSubmitting"
          >
            <span v-if="isSubmitting" class="flex items-center gap-2">
              <span class="w-4 h-4 border-2 border-foreground/30 border-t-foreground rounded-full animate-spin" />
              {{ t("landing.contact.form.submitting") }}
            </span>
            <span v-else class="flex items-center gap-2">
              <Send class="w-4 h-4" />
              {{ formData.requestType === "demo" ? t("landing.contact.form.submit_demo") : t("landing.contact.form.submit_trial") }}
            </span>
          </Button>

          <p class="text-xs text-muted-foreground text-center mt-4">
            {{ t("landing.contact.form.privacy_notice") }}
          </p>
        </form>
      </AnimatedSection>
    </div>
  </section>
</template>
